import { Box, Modal } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { useEffect, useState, forwardRef, useImperativeHandle, useRef } from "react";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import Swal from "sweetalert2";
import {
  completePayment,
  fetchConsumedDays,
  odsSearch,
  updateOrderTimeline,
} from "../../../config/Services";
import { useSnackbar } from "../product/hooks/SnackbarProvider";
import BoardItem from "./components/BoardItem";
import SkeletonLoader from "./components/SkeletonLoader";
import "./delivery.css";
import BarcodeReader from "react-barcode-reader";
import { useSelector } from "react-redux";
import { getBoardItem, getOdsText } from "../product/all-products/redux/Reducers/paginationReducer";
import { setBoardItem, setOdsNotif } from "../product/all-products/redux/Actions/paginationActions";
import BoardItemDetails from "./components/BoardItemDetails";
import breakpoints from "../../../assets/theme/base/breakpoints";
import { useDispatch } from "react-redux";
import usePrint from "../product/hooks/usePrint";
import { PosCustomButton } from "../../../customComponents/PosCustomButton/PosCustomButton";
import createdOdsIcon from "../../../assets/images/ods_created.png";
import preparingOdsIcon from "../../../assets/images/ods_chef.png";
import packedOdsIcon from "../../../assets/images/ods_packed.png";
import transitOdsIcon from "../../../assets/images/ods_transit.png";
import deliveredOdsIcon from "../../../assets/images/ods_delivered.png";
import OdsDeliveryTable from "./components/OdsDeliveryTable";
import { stopOrderAlert } from "../../../utils/notificationSystem/orderAlertAudio";
import {
  fetchAllOrdersAndSlots,
  manualOrderRefresh,
  subscribeOrderUpdates,
  setPollingDate,
} from "../../../utils/odsArchitecture/orderPollingService";
import { handleOrderNotification } from "../../../utils/notificationSystem/orderNotification";
import { fetchSlots } from "../../../utils/odsArchitecture/odsSlotService";
import SlotList from "../product/all-products/components/SlotItem/SlotList";
import {
  startNotificationPolling,
  subscribeOrderNotification,
} from "../../../utils/notificationSystem/orderNotificationPollingService";

const newSwal = Swal.mixin({
  customClass: {
    confirmButton: "button button-success",
    cancelButton: "button button-error",
    container: "my-swal-container",
  },
  buttonsStyling: false,
});

const reorder = (list, startIndex, endIndex) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);

  return result;
};

const move = (source, destination, droppableSource, droppableDestination) => {
  const sourceClone = Array.from(source);
  const destClone = Array.from(destination);

  const [removed] = sourceClone.splice(droppableSource.index, 1);
  destClone.splice(droppableDestination.index, 0, removed);

  return [sourceClone, destClone];
};
const grid = 8;

const getItemStyle = (isDragging, draggableStyle) => ({
  userSelect: "none",
  margin: `0 0 ${grid}px 0`,
  borderRadius: "16px",
  position: "relative",
  overflow: "hidden",
  background: isDragging ? "lightgreen" : "white",
  boxShadow: isDragging ? "0 8px 24px rgba(0, 0, 0, 0.15)" : undefined,
  ...draggableStyle,
});

const getListStyle = (isDraggingOver, color) => ({
  width: "100%",
  overflow: "auto",
  maxHeight: "100%",
});

// Helper function to get display name for status (only for restaurants)
const getStatusDisplayName = (status, isRestaurant) => {
  if (!isRestaurant) return status;

  const statusMap = {
    CREATED: isRestaurant ? "NEW ORDER" : "NEW",
    ACCEPTED: "PREPARING",
    PACKAGED: isRestaurant ? "FOOD IS READY" : "PACKAGED",
    IN_TRANSIT: isRestaurant ? "PICKED UP" : "IN TRANSIT",
    DELIVERED: "DELIVERED",
  };
  return statusMap[status] || status;
};

const DeliveryV2 = forwardRef(({ odsDate, setHasFutureOrders }, ref) => {
  const locId = localStorage.getItem("locId");
  const orgId = localStorage.getItem("orgId");
  const [ordersList, setOrdersList] = useState([]);
  const [isDeliveryModalOpen, setIsDeliveryModalOpen] = useState(false);

  const userName = localStorage.getItem("user_name");
  const retailType = localStorage.getItem("retailType");
  const branchSubType = localStorage.getItem("branchSubType");

  const baseArray = [
    {
      status: "CREATED",
      data: [],
      color: "#0860E6",
      icon: createdOdsIcon,
    },
    ...(retailType === "RESTAURANT"
      ? [
          {
            status: "ACCEPTED",
            data: [],
            color: "#D4BD19",
            icon: preparingOdsIcon,
          },
        ]
      : []),
    {
      status: "PACKAGED",
      data: [],
      color: "#EBA85A",
      icon: packedOdsIcon,
    },
    {
      status: "IN_TRANSIT",
      data: [],
      color: "#E90000",
      icon: transitOdsIcon,
    },
    {
      status: "DELIVERED",
      data: [],
      color: "#0B742C",
      icon: deliveredOdsIcon,
    },
  ];

  const [state, setState] = useState(baseArray);
  const [shouldRefresh, setShouldRefresh] = useState(true);
  const boardItem = useSelector(getBoardItem);
  const odsSearchText = useSelector(getOdsText);
  const dispatch = useDispatch();
  const { startPrint, printToken, printItemLabels } = usePrint();

  const ordersListRef = useRef([]);
  // Client-side record of the sequence in which orders were moved to DELIVERED,
  // so the most recently delivered order group stays on top across refreshes.
  const deliveredSeqRef = useRef({});
  const deliveredSeqCounterRef = useRef(0);
  const markDelivered = (orderId) => {
    if (!orderId) return;
    deliveredSeqRef.current[orderId] = ++deliveredSeqCounterRef.current;
  };
  // Mirrors the search text so async callbacks (polling updates, stale search
  // responses) can tell whether a search is still active.
  const odsSearchTextRef = useRef("");
  const isLabelPrinterEnabled =
    localStorage.getItem("retailType") !== "GROCERY" &&
    JSON.parse(localStorage.getItem("appConfig") || "{}")?.all?.LABEL_PRINTER?.flag === true;
  const [validationModal, setValidationModal] = useState(false);
  const [deliveredModalData, setDeliveredModalData] = useState([]);
  const [isManualRefreshing, setIsManualRefreshing] = useState(false);
  const [cancelLoadingId, setCancelLoadingId] = useState(null);
  // const [riderCancelled, setRiderCancelled] = useState(false);

  const [statusCount, setStatusCount] = useState({
    CREATED: 0,
    ...(retailType === "RESTAURANT" ? { ACCEPTED: 0 } : {}),
    PACKAGED: 0,
    IN_TRANSIT: 0,
    DELIVERED: 0,
  });

  const isMobile = window.innerWidth < breakpoints.values.sm;

  // Board details modal style
  const style = {
    position: isMobile ? "static" : "absolute",
    top: "50%",
    right: "-35%",
    width: isMobile ? "100%" : "70%",
    transform: isMobile ? "none" : "translate(-50%, -50%)",
    height: "100%",
    overflowY: retailType === "RESTAURANT" ? "hidden" : "auto",
    bgcolor: "#fff",
    // border: "1px solid #E6E6E6",
    p: "1.5rem",

    ...(isMobile && {
      height: "100%",
      display: "flex",
      flexDirection: "column",
      justifyContent: "center",
    }),
  };

  const showSnackbar = useSnackbar();

  const applyOrdersList = (newList) => {
    setOrdersList(newList);
    ordersListRef.current = newList;

    const formattedData = formatData(newList);
    setState(formattedData);
    updateStatusCount(formattedData);
  };

  const runOdsSearch = (query) => {
    setIsManualRefreshing(true);
    odsSearch({
      page: 0,
      pageSize: 99,
      destinationLocationId: locId,
      query,
    })
      .then((res) => {
        // Ignore stale responses (query changed or search was cleared meanwhile)
        if (odsSearchTextRef.current !== query) return;
        applyOrdersList(res?.data?.data?.orderResponseList || []);
      })
      .catch((error) => {
        showSnackbar(error?.response?.data?.message || error?.message, "error");
      })
      .finally(() => {
        setIsManualRefreshing(false);
      });
  };

  useImperativeHandle(ref, () => ({
    refreshOrders: () => {
      if (odsSearchTextRef.current) {
        runOdsSearch(odsSearchTextRef.current);
        return;
      }
      setIsManualRefreshing(true);
      manualOrderRefresh();
    },
  }));

  const formatData = (data) => {
    let arrayData = JSON.parse(JSON.stringify(baseArray));

    data.forEach((item) => {
      let status = item?.baseOrderResponse?.fulfilmentStatus;

      // Map PREPARING status to ACCEPTED column for display
      if (status === "PREPARING") {
        status = "ACCEPTED";
      }

      const index = arrayData.findIndex((el) => el?.status === status);

      if (index !== -1) {
        arrayData[index]?.data.push({
          id: item?.baseOrderResponse?.orderId,
          loading: false,
          content: {
            ...item?.baseOrderResponse,
            ...item?.addressEntityModel,
            isAssigned: item?.orderTokenDetails?.isAssigned,
            token: item?.orderTokenDetails?.token,
            grandTotal: item?.orderBillingDetails?.grandTotal,
            paymentMethod: item?.orderBillingDetails?.paymentMethod,
            // Keep the original status for display purposes
            originalStatus: item?.baseOrderResponse?.fulfilmentStatus,
          },
        });
      }
    });

    // Show the most recently delivered order group on top of the DELIVERED column.
    // Orders delivered during this session are ordered by the drag sequence we
    // recorded on the client (latest drag first); the rest fall back to timestamp.
    const deliveredIdx = arrayData?.findIndex((el) => el?.status === "DELIVERED");
    if (deliveredIdx !== -1) {
      arrayData[deliveredIdx]?.data?.sort((a, b) => {
        const seqA = deliveredSeqRef.current[a?.id];
        const seqB = deliveredSeqRef.current[b?.id];
        if (seqA != null && seqB != null) return seqB - seqA;
        if (seqA != null) return -1;
        if (seqB != null) return 1;
        return (
          new Date(b?.content?.updatedAt || b?.content?.createdAt) -
          new Date(a?.content?.updatedAt || a?.content?.createdAt)
        );
      });
    }

    return arrayData;
  };

  const updateStatusCount = (data) => {
    const count = data.reduce((acc, el) => {
      acc[el?.status] = el?.data?.length;
      return acc;
    }, {});
    setStatusCount(count);
  };

  const updateOrderMutation = useMutation({
    mutationFn: async (payload) => {
      setShouldRefresh(false);
      const res = await updateOrderTimeline(payload);
      if (res?.data?.code === "ECONNRESET") {
        throw new Error(res?.data?.message);
      }
      if (res?.data?.data?.es) {
        throw new Error(res?.data?.data?.message);
      }
      return res;
    },
  });

  const recordPaymentMutation = useMutation({
    mutationFn: (payload) => {
      if (retailType === "RESTAURANT") {
        return Promise.resolve({ skip: true });
      }
      return completePayment(payload);
    },
    onSuccess: (res) => {
      if (res?.skip) return;
      if (res?.data?.data?.es) {
        showSnackbar(res?.data?.data?.message, "error");
        return;
      }
      showSnackbar("Payment recorded", "success");
    },
    onError: (error) => {
      showSnackbar(error?.response?.data?.message || error?.message, "error");
    },
  });

  const handleCloseBoardItem = () => {
    dispatch(setBoardItem(null));
    setShouldRefresh(true);
  };

  /** Re-fetch slots when user picks a different date (initial fetch is handled by SlotList) */
  useEffect(() => {
    if (!odsDate) return;
    fetchSlots({
      date: odsDate,
      orgId: localStorage.getItem("orgId"),
      locId: localStorage.getItem("locId"),
    });
  }, [odsDate]);

  useEffect(() => {
    const unsubscribe = subscribeOrderUpdates(({ data, hasCreatedOrders }) => {
      // While a search is active the board shows search results; don't let the
      // background polling overwrite them.
      if (odsSearchTextRef.current) return;

      applyOrdersList(data?.orderResponseList || []);

      setIsManualRefreshing(false);
    });

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    fetchAllOrdersAndSlots();
    const unsubscribe = subscribeOrderNotification(
      ({ hasNewOrder, hasCreatedOrders, hasFutureOrders: hasFutureOrdersUpdate }) => {
        setHasFutureOrders(hasFutureOrdersUpdate ?? false);
        handleOrderNotification({
          retailType,
          isDeliveryScreen: true,
          hasNewOrder,
          hasCreatedOrders,
          setBlinkIcon: (value) => dispatch(setOdsNotif(value)),
          showPopup: () => {
            newSwal
              .fire({
                title: "New order placed",
                icon: "info",
                confirmButtonText: "OK",
              })
              .then((result) => {
                if (result.isConfirmed) {
                  stopOrderAlert();

                  if (retailType === "GROCERY") {
                    const latestOrderId = ordersListRef.current?.[0]?.baseOrderResponse?.orderId;
                    if (latestOrderId) {
                      startPrint({
                        orderId: latestOrderId,
                        printType: "picklist",
                      });
                    }
                  }

                  //Refresh after user acknowledges
                  fetchAllOrdersAndSlots();
                }
              });
          },
        });

        //Fetch immediately when new order arrives
        if (hasNewOrder) {
          fetchAllOrdersAndSlots();
        }
      }
    );

    startNotificationPolling();

    return () => {
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    setPollingDate(odsDate); // update the module-level date
    fetchAllOrdersAndSlots(); // immediately re-fetch with the new date
  }, [odsDate]);

  // Global search (navbar) for the ODS board: search via odsSearch API while a
  // query is set; restore the live polled board when it's cleared.
  useEffect(() => {
    const previousQuery = odsSearchTextRef.current;
    odsSearchTextRef.current = odsSearchText;

    if (odsSearchText) {
      runOdsSearch(odsSearchText);
    } else if (previousQuery) {
      // Search was cleared — re-fetch the live board immediately.
      fetchAllOrdersAndSlots();
    }
  }, [odsSearchText]);

  const onDragEnd = (result) => {
    const { source, destination } = result;

    const sInd = +source.droppableId;
    const dInd = +destination.droppableId;

    if (!destination) {
      return;
    }

    if (dInd < sInd) {
      showSnackbar("Orders cannot be reverted to previous status", "warning");
      return;
    }

    if (dInd - sInd > 1) {
      showSnackbar("Skipping order status is not allowed", "warning");
      return;
    }

    if (sInd === dInd) {
      const items = reorder(state[sInd].data, source.index, destination.index);
      const newState = [...state];
      newState[sInd].data = items;
      setState(newState);
    } else {
      const [sourceData, destData] = move(state[sInd].data, state[dInd].data, source, destination);
      const newState = [...state];

      const draggedItem = newState[sInd].data[source.index];
      draggedItem.loading = true;
      const [tempSourceData, tempDestData] = [newState[sInd].data, newState[dInd].data];
      newState[sInd].data = sourceData;
      newState[dInd].data = destData;
      updateOrderMutation.mutate(
        {
          orderId: draggedItem.id,
          orderStatus: state[dInd].status,
          updatedBy: userName,
          ...(retailType === "RESTAURANT" && { source: "ODS" }),
        },
        {
          onSuccess: (response) => {
            const responseData = response?.data?.data;
            if (responseData?.showPopup) {
              const readyAtIST = new Date(`${responseData?.readyAt}:00Z`).toLocaleString("en-IN", {
                timeZone: "Asia/Kolkata",
                day: "2-digit",
                month: "2-digit",
                year: "numeric",
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              });
              const [datePart, timePart] = readyAtIST.split(", ");
              const formattedDate = datePart.replace(/\//g, "-");
              const formattedTime = timePart.toUpperCase();
              const formattedReadyAt = `${formattedDate}, ${formattedTime}`;
              newSwal
                .fire({
                  title: "Order Cannot Be Moved",
                  text: `Order cannot be moved to Preparing before: ${formattedReadyAt}`,
                  icon: "warning",
                  confirmButtonText: "OK",
                })
                .then(() => {
                  manualOrderRefresh();
                });
              return;
            }
            showSnackbar("Order status updated successfully", "success");
            if (state[sInd].status === "CREATED" && state[dInd].status === "ACCEPTED") {
              stopOrderAlert();
            }
            if (state[sInd].status === "CREATED" && state[dInd].status === "ACCEPTED") {
              startPrint({
                orderId: draggedItem.id,
                printType: "invoice",
                ...(draggedItem?.content?.subOrderType === "DELIVERY" && {
                  orderSource: "b2c",
                }),
              });
            }
            if (state[dInd].status === "PACKAGED" && retailType !== "RESTAURANT") {
              startPrint({ orderId: draggedItem.id, printType: "invoice", orderSource: "b2c" });
            }
            if (state[dInd].status === "DELIVERED") {
              markDelivered(draggedItem?.id);
              const payload = {
                referenceId: draggedItem?.id,
                paymentMethod: "CASH",
                paymentMode: "COD",
                amountPaid: draggedItem?.content?.grandTotal,
                paymentStatus: "COMPLETED",
              };
              recordPaymentMutation.mutate(payload);
            }
            manualOrderRefresh();
          },
          onError: (error) => {
            newState[sInd].data = tempSourceData;
            newState[dInd].data = tempDestData;
            showSnackbar(error?.response?.data?.message || error?.message, "error");
          },
          onSettled: () => {
            draggedItem.loading = false;
            setState(newState);
            setShouldRefresh(true);
          },
        }
      );
    }
  };

  const moveItemToState = (itemId, sourceState, destinationState, onSuccessCallback) => {
    updateOrderMutation.mutate(
      {
        orderId: itemId,
        orderStatus: destinationState,
        updatedBy: userName,
      },
      {
        onSuccess: () => {
          if (destinationState === "DELIVERED") markDelivered(itemId);
          manualOrderRefresh();
          onSuccessCallback?.();
        },
      }
    );
    setState([...state]);
  };

  const deliveredIndex = state.findIndex((el) => el.status === "DELIVERED");
  const deliveredAll = deliveredIndex !== -1 ? state[deliveredIndex].data : [];
  const deliveredLatest10 = deliveredAll.slice(0, 10);

  const handleDeliveryModalOpen = (data) => {
    setDeliveredModalData(data);
    setIsDeliveryModalOpen(true);
  };

  const handleCancelOrder = (orderId) => {
    setCancelLoadingId(orderId);

    updateOrderMutation.mutate(
      {
        orderId: orderId,
        orderStatus: "CANCELLED",
        updatedBy: userName,
        ...(retailType === "RESTAURANT" && { source: "ODS" }),
      },
      {
        onSuccess: () => {
          showSnackbar("Order canceled successfully", "success");
          manualOrderRefresh();
          handleCloseBoardItem();
          stopOrderAlert();
        },
        onError: (error) => {
          showSnackbar(error?.response?.data?.message || error?.message, "error");
        },
        onSettled: () => {
          setCancelLoadingId(null);
        },
      }
    );
  };

  const handleAcceptOrder = (itemContent) => {
    updateOrderMutation.mutate(
      {
        orderId: itemContent?.orderId,
        orderStatus: "ACCEPTED",
        updatedBy: userName,
        ...(retailType === "RESTAURANT" && { source: "ODS" }),
      },
      {
        onSuccess: (response) => {
          const responseData = response?.data?.data;
          if (responseData?.showPopup) {
            const readyAtIST = new Date(`${responseData?.readyAt}:00Z`).toLocaleString("en-IN", {
              timeZone: "Asia/Kolkata",
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "numeric",
              minute: "2-digit",
              hour12: true,
            });
            const [datePart, timePart] = readyAtIST.split(", ");
            const formattedReadyAt = `${datePart?.replace(/\//g, "-")}, ${timePart?.toUpperCase()}`;
            newSwal
              .fire({
                title: "Order Cannot Be Moved",
                text: `Order cannot be moved to Preparing before: ${formattedReadyAt}`,
                icon: "warning",
                confirmButtonText: "OK",
              })
              .then(() => manualOrderRefresh());
            return;
          }
          showSnackbar("Order status updated successfully", "success");
          stopOrderAlert();
          startPrint({
            orderId: itemContent?.orderId,
            printType: "invoice",
            ...(itemContent?.subOrderType === "DELIVERY" && { orderSource: "b2c" }),
          });
          manualOrderRefresh();
        },
        onError: (error) => {
          showSnackbar(error?.response?.data?.message || error?.message, "error");
        },
      }
    );
  };

  return (
    <>
      <div className="board-container">
        {!validationModal && (
          <BarcodeReader
            onScan={(sc) => {
              let scannedOrderId = null;
              try {
                scannedOrderId = JSON.parse(sc)?.orderId;
              } catch (e) {
                // Not a picklist QR (e.g. a product barcode) — ignore
                return;
              }
              if (!scannedOrderId) return;

              // The picklist QR carries the status at print time (always CREATED), so
              // look the order up by id only and open it in whatever column it's in now.
              const filterItem = ordersListRef.current?.filter(
                (el) => el?.baseOrderResponse?.orderId === scannedOrderId
              );
              const content = formatData(filterItem || []).find((el) => el?.data?.length > 0)
                ?.data?.[0]?.content;

              if (content) {
                dispatch(setBoardItem(content));
              } else {
                showSnackbar("Order not found", "error");
              }
            }}
            avgTimeByChar={120}
          />
        )}
        <div className="board-slots-container">
          <SlotList />
        </div>
        <div className={`board-items-container ${retailType === "RESTAURANT" ? "fiveGrid" : ""}`}>
          <DragDropContext onDragEnd={onDragEnd}>
            {state.map((el, ind) => {
              const listToRender = el?.status === "DELIVERED" ? deliveredLatest10 : el?.data;
              return (
                <Droppable key={ind} droppableId={`${ind}`}>
                  {(provided, snapshot) => (
                    <div
                      className="board-items"
                      ref={provided.innerRef}
                      style={getListStyle(snapshot.isDraggingOver, el?.color)}
                      {...provided.droppableProps}
                    >
                      <div className="status-outer-parent">
                        <div className="status-icon-title-outer-parent">
                          <div
                            className="ods-status-legend"
                            style={{ backgroundColor: el?.color }}
                          ></div>
                          <div className="status-title">
                            {getStatusDisplayName(el?.status, retailType === "RESTAURANT")}
                          </div>
                        </div>
                        <div className="status-title-number">{`${el?.data?.length}`}</div>
                      </div>
                      {isManualRefreshing ? (
                        <SkeletonLoader type="board" />
                      ) : (
                        listToRender?.map((item, index) => (
                          <Draggable key={item.id} draggableId={item.id} index={index}>
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                                className={`board-item-${ind}`}
                                style={{
                                  ...getItemStyle(
                                    snapshot.isDragging,
                                    provided.draggableProps.style
                                  ),
                                  pointerEvents: item?.loading ? "none" : "auto",
                                }}
                              >
                                <>
                                  <BoardItem
                                    item={item?.content}
                                    isLoading={item?.loading}
                                    setShouldRefresh={setShouldRefresh}
                                    onCancelOrder={handleCancelOrder}
                                    cancelLoading={cancelLoadingId === item?.content?.orderId}
                                    onPrintLabels={
                                      isLabelPrinterEnabled ? printItemLabels : undefined
                                    }
                                    isFirstCreatedCard={el?.status === "CREATED" && index === 0}
                                    onAcceptOrder={handleAcceptOrder}
                                  />
                                </>
                              </div>
                            )}
                          </Draggable>
                        ))
                      )}
                      {el?.status === "DELIVERED" && deliveredAll?.length > 10 && (
                        <div className="viewAllButton">
                          <PosCustomButton
                            title="View All"
                            variant="black-s"
                            width={"100%"}
                            radius={"6px"}
                            p={"8px 12px"}
                            onClickFunction={() => handleDeliveryModalOpen(deliveredAll)}
                          />
                        </div>
                      )}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              );
            })}
          </DragDropContext>
        </div>
        {boardItem && (
          <Modal
            open={boardItem}
            onClose={handleCloseBoardItem}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
          >
            <Box sx={style} className="delivery-board-modal-box">
              <BoardItemDetails
                item={boardItem}
                handleClose={handleCloseBoardItem}
                setValidationModal={setValidationModal}
                validationModal={validationModal}
                moveItemToState={moveItemToState}
                startPrint={startPrint}
                onCancelOrder={handleCancelOrder}
                cancelLoading={cancelLoadingId === boardItem?.orderId}
                // riderCancelled={riderCancelled}
                // setRiderCancelled={setRiderCancelled}
              />
            </Box>
          </Modal>
        )}
        <OdsDeliveryTable
          isDeliveryModalOpen={isDeliveryModalOpen}
          setIsDeliveryModalOpen={setIsDeliveryModalOpen}
          deliveredModalData={deliveredModalData}
        />
      </div>
    </>
  );
});

export default DeliveryV2;
